import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Container, Button, Modal, Form, Spinner } from "react-bootstrap";
import { IoMdRefresh } from "react-icons/io";
import { BsFillKanbanFill } from "react-icons/bs";
import { TbLabelImportantFilled } from "react-icons/tb";
import {
  MdAdd,
  MdEdit,
  MdDelete,
  MdSource,
  MdSearch,
  MdSecurity,
  MdTune,
  MdClose,
  MdCheckCircle,
  MdErrorOutline,
  MdInbox,
  MdVerifiedUser,
} from "react-icons/md";
import { useTheme } from "../components/ThemeContext";
import NavMenu from "../components/NavMenu";
import { accessibleBg, bestTextOn, normalizeHex } from "../utils/color";
import axios from "axios";
import "./insights.css";
import "./dataCenterPage.css";

const API = process.env.REACT_APP_API || "";

const EP_GET_PRIVACY = `${API}/getPrivacySettings`;
const EP_UPDATE_PRIVACY = `${API}/updatePrivacySettings`;

const DEFAULT_COLOR = "#1a3483";

const COLOR_PRESETS = [
  "#1a3483",
  "#3D74F5",
  "#28a745",
  "#ffc107",
  "#fd7e14",
  "#dc3545",
  "#6f42c1",
  "#20c997",
  "#0dcaf0",
  "#6c757d",
];

const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

// One entry per list; everything the table, the form and the API calls need.
const LISTS = {
  priority: {
    key: "priority",
    label: "Priority",
    noun: "priority",
    plural: "priorities",
    blurb: "Urgency levels for enquiries",
    icon: BsFillKanbanFill,
    accent: "accent-blue",
    nameKey: "priority",
    nameLabel: "Priority",
    columnLabel: "Priority",
    hasColor: false,
    hasDescription: true,
    namePlaceholder: "e.g. High",
    idKey: "priority_id",
    idOf: (r) => r?.priority_id ?? r?.id ?? null,
    ep: {
      get: `${API}/getPriority`,
      save: `${API}/savePriority`,
      update: `${API}/updatePriority`,
      del: `${API}/deletePriority`,
    },
    toPayload: (f) => ({ priority: f.name, description: f.description }),
    requiredMessage: "Priority and description are required.",
  },
  label: {
    key: "label",
    label: "Tag",
    noun: "tag",
    plural: "tags",
    blurb: "Labels to categorise enquiries",
    icon: TbLabelImportantFilled,
    accent: "accent-violet",
    nameKey: "label_name",
    nameLabel: "Tag name",
    columnLabel: "Tag",
    hasColor: true,
    hasDescription: true,
    namePlaceholder: "e.g. Follow up",
    idKey: "label_id",
    idOf: (r) => r?.label_id ?? null,
    ep: {
      get: `${API}/getLabels`,
      save: `${API}/saveLabel`,
      update: `${API}/updateLabel`,
      del: `${API}/deleteLabel`,
    },
    toPayload: (f) => ({ label_name: f.name, description: f.description, color: f.color || null }),
    requiredMessage: "Tag name and description are required.",
  },
  source: {
    key: "source",
    label: "Source",
    noun: "source",
    plural: "sources",
    blurb: "Where enquiries come from",
    icon: MdSource,
    accent: "accent-teal",
    nameKey: "source",
    nameLabel: "Source name",
    columnLabel: "Source",
    hasColor: true,
    hasDescription: false,
    namePlaceholder: "e.g. Facebook",
    idKey: "source_id",
    idOf: (r) => r?.source_id ?? null,
    ep: {
      get: `${API}/getSources`,
      save: `${API}/saveSources`,
      update: `${API}/updateSource`,
      del: `${API}/deleteSource`,
    },
    toPayload: (f) => ({ source: f.name, color: f.color || null }),
    requiredMessage: "Source name is required.",
  },
};

const PRIVACY_TAB = {
  key: "privacy",
  label: "Privacy & Security",
  blurb: "Control how AI handles your data",
  icon: MdSecurity,
  accent: "accent-green",
};

const NAV = [LISTS.priority, LISTS.label, LISTS.source, PRIVACY_TAB];

const EMPTY_FORM = { name: "", description: "", color: DEFAULT_COLOR };

function formatDDMMYYYY(input) {
  if (!input) return "-";

  const s = String(input).trim();
  let dt;

  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const iso = s.includes("T") ? s : s.replace(" ", "T");
    dt = new Date(iso);
    if (isNaN(dt.getTime())) dt = new Date(s.substring(0, 10));
  } else {
    dt = new Date(s);
  }

  if (isNaN(dt.getTime())) return s;

  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear();

  return `${dd}-${mm}-${yyyy}`;
}

// A tag/source name painted in its own (user-chosen) color, nudged only when needed so
// its text stays readable.
const ColorPill = ({ color, children }) => {
  const bg = accessibleBg(normalizeHex(color) || DEFAULT_COLOR);
  return (
    <span className="dcp-pill" style={{ background: bg, color: bestTextOn(bg) }}>
      {children}
    </span>
  );
};

const ConfirmModal = ({ isDark, show, title, children, confirmLabel, busy, onConfirm, onCancel }) => (
  <Modal show={show} onHide={busy ? undefined : onCancel} centered className={isDark ? "dcp-modal-dark" : ""}>
    <Modal.Header closeButton={!busy} className="dcp-modal-head">
      <Modal.Title className="dcp-modal-title">{title}</Modal.Title>
    </Modal.Header>
    <Modal.Body className="dcp-modal-body">{children}</Modal.Body>
    <Modal.Footer className="dcp-modal-foot">
      <Button className="dcp-btn dcp-btn-ghost" onClick={onCancel} disabled={busy}>
        Cancel
      </Button>
      <Button className="dcp-btn dcp-btn-danger" onClick={onConfirm} disabled={busy}>
        {busy ? <Spinner size="sm" animation="border" className="me-2" /> : null}
        {confirmLabel}
      </Button>
    </Modal.Footer>
  </Modal>
);

const PrivacyPanel = ({ enabled, loading, saving, error, onToggle, onRetry }) => (
  <section className="dcp-card dcp-panel accent-green dcp-fade" aria-labelledby="dcp-privacy-title">
    <header className="dcp-panel-head">
      <span className="dcp-panel-icon"><MdSecurity /></span>
      <div className="dcp-panel-titles">
        <h2 className="dcp-panel-title" id="dcp-privacy-title">Data Privacy &amp; Security</h2>
      </div>
    </header>

    {loading ? (
      <div className="dcp-state">
        <Spinner animation="border" size="sm" />
        <span>Loading privacy settings...</span>
      </div>
    ) : enabled === null ? (
      <div className="dcp-state dcp-state-error">
        <MdErrorOutline aria-hidden="true" />
        <span>{error || "Couldn't load privacy settings."}</span>
        <Button className="dcp-btn dcp-btn-outline dcp-btn-sm" onClick={onRetry}>
          <IoMdRefresh className="me-1" /> Retry
        </Button>
      </div>
    ) : (
      <>
        <div className={`dcp-privacy ${enabled ? "is-on" : "is-off"}`}>
          <div className="dcp-privacy-top">
            <span className="dcp-privacy-shield"><MdVerifiedUser /></span>
            <div className="dcp-privacy-text">
              <div className="dcp-privacy-title">Sensitive Data Protection</div>
              <span className={`dcp-status ${enabled ? "ok" : "warn"}`}>
                {enabled ? "Protection is on" : "Protection is off"}
              </span>
            </div>

            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              aria-label="Sensitive Data Protection"
              className={`dc-switch ${enabled ? "on" : ""}`}
              onClick={() => onToggle(!enabled)}
              disabled={saving}
            >
              <span className="dc-switch-label">{enabled ? "ON" : "OFF"}</span>
              <span className="dc-switch-knob" />
            </button>
          </div>

          <ul className="dcp-privacy-list">
            <li><MdCheckCircle aria-hidden="true" /> OpenAI will not store personal data or responses</li>
            <li>
              <MdCheckCircle aria-hidden="true" /> Covers all AI features: reply and template
              suggestions, WhatsApp summaries and insights
            </li>
            <li><MdCheckCircle aria-hidden="true" /> Each request is sent on its own</li>
          </ul>
        </div>

        {saving && (
          <div className="dcp-state dcp-state-inline">
            <Spinner animation="border" size="sm" />
            <span>Saving...</span>
          </div>
        )}
        {error && <div className="dcp-inline-error" role="alert">{error}</div>}
      </>
    )}
  </section>
);

const DataCenter = () => {
  const { isDark } = useTheme();

  const [tab, setTab] = useState("priority");

  const [data, setData] = useState({ priority: [], label: [], source: [] });
  const [loading, setLoading] = useState({ priority: false, label: false, source: false });

  const [search, setSearch] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [editingRow, setEditingRow] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");

  const [confirm, setConfirm] = useState(null); // { kind: "delete", row } | { kind: "privacy-off" }
  const [confirmBusy, setConfirmBusy] = useState(false);

  const [toast, setToast] = useState(null); // { type: "success" | "error", text }
  const toastTimer = useRef(null);

  // null = not loaded yet / failed to load (so we never show a guess)
  const [privacyOn, setPrivacyOn] = useState(null);
  const [loadingPrivacy, setLoadingPrivacy] = useState(false);
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [privacyError, setPrivacyError] = useState("");

  const cfg = LISTS[tab] || null; // null on the privacy tab

  const notify = useCallback((type, text) => {
    clearTimeout(toastTimer.current);
    setToast({ type, text });
    toastTimer.current = setTimeout(() => setToast(null), 4200);
  }, []);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const norm = (v) =>
    String(v ?? "")
      .toLowerCase()
      .trim();

  const fetchList = useCallback(async (key) => {
    setLoading((s) => ({ ...s, [key]: true }));
    try {
      const res = await axios.get(LISTS[key].ep.get);
      if (res.data?.success) setData((s) => ({ ...s, [key]: res.data.data || [] }));
    } catch (err) {
      console.error(`${LISTS[key].ep.get} error:`, err);
    } finally {
      setLoading((s) => ({ ...s, [key]: false }));
    }
  }, []);

  const fetchPrivacy = useCallback(async () => {
    setLoadingPrivacy(true);
    setPrivacyError("");
    try {
      const res = await axios.get(EP_GET_PRIVACY);
      if (res.data?.success) setPrivacyOn(!!res.data.data?.sensitiveDataProtection);
      else setPrivacyError(res.data?.message || "Couldn't load privacy settings.");
    } catch (err) {
      console.error("getPrivacySettings error:", err);
      setPrivacyError(err?.response?.data?.message || "Couldn't load privacy settings.");
    } finally {
      setLoadingPrivacy(false);
    }
  }, []);

  const savePrivacy = async (next) => {
    const previous = privacyOn;
    setPrivacyOn(next); // optimistic; rolled back below if the save fails
    setSavingPrivacy(true);
    setPrivacyError("");
    try {
      const res = await axios.post(EP_UPDATE_PRIVACY, { sensitiveDataProtection: next });
      if (!res.data?.success) throw new Error(res.data?.message || "Failed to save.");
      setPrivacyOn(!!res.data.data?.sensitiveDataProtection);
      notify("success", next ? "Sensitive Data Protection is on." : "Sensitive Data Protection is off.");
    } catch (err) {
      setPrivacyOn(previous);
      setPrivacyError(
        err?.response?.data?.message || err.message || "Failed to save privacy settings.",
      );
    } finally {
      setSavingPrivacy(false);
    }
  };

  const handleTogglePrivacy = (next) => {
    if (!next) setConfirm({ kind: "privacy-off" });
    else savePrivacy(true);
  };

  useEffect(() => {
    fetchList("priority");
    fetchList("label");
    fetchList("source");
    fetchPrivacy();
  }, [fetchList, fetchPrivacy]);

  useEffect(() => {
    setSearch("");
  }, [tab]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormError("");
    setEditingRow(null);
    setModalMode("create");
    setShowModal(true);
  };

  const openEdit = (row) => {
    setForm({
      name: row?.[cfg.nameKey] ?? "",
      description: row?.description ?? "",
      color: row?.color || DEFAULT_COLOR,
    });
    setFormError("");
    setEditingRow(row);
    setModalMode("edit");
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
  };

  const setField = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [field]: value }));
    if (formError) setFormError("");
  };

  const handleSave = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!cfg) return;

    const trimmed = {
      name: form.name.trim(),
      description: form.description.trim(),
      color: cfg.hasColor ? normalizeHex(form.color) : "",
    };

    if (!trimmed.name || (cfg.hasDescription && !trimmed.description)) {
      setFormError(cfg.requiredMessage);
      return;
    }
    if (cfg.hasColor && trimmed.color && !HEX_RE.test(trimmed.color)) {
      setFormError("Enter a valid hex color, for example #1a3483.");
      return;
    }

    const isCreate = modalMode === "create";
    const payload = cfg.toPayload(trimmed);

    if (!isCreate) {
      const id = cfg.idOf(editingRow);
      if (!id) {
        setFormError(`${cfg.idKey} not found.`);
        return;
      }
      payload[cfg.idKey] = id;
    }

    setSaving(true);
    try {
      const res = await axios.post(isCreate ? cfg.ep.save : cfg.ep.update, payload);

      if (res.data?.success) {
        notify(
          "success",
          res.data?.message ||
            (isCreate ? `New ${cfg.noun} created successfully!` : `${cfg.label} updated successfully!`),
        );
        setShowModal(false);
        fetchList(cfg.key);
      } else {
        setFormError(res.data?.message || `Failed to ${isCreate ? "create" : "update"} ${cfg.noun}.`);
      }
    } catch (err) {
      setFormError(err?.response?.data?.message || "Server error. Please try again.");
      console.error("save/update error:", err?.response?.data || err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const id = cfg.idOf(row);
    if (!id) {
      setConfirm(null);
      notify("error", `${cfg.idKey} not found.`);
      return;
    }

    setConfirmBusy(true);
    try {
      const res = await axios.post(cfg.ep.del, { [cfg.idKey]: id });

      if (res.data?.success) {
        notify("success", res.data?.message || `${cfg.label} deleted successfully!`);
        fetchList(cfg.key);
      } else {
        notify("error", res.data?.message || `Failed to delete ${cfg.noun}.`);
      }
    } catch (err) {
      console.error("delete error:", err?.response?.data || err);
      notify("error", err?.response?.data?.message || "Server error. Delete failed.");
    } finally {
      setConfirmBusy(false);
      setConfirm(null);
    }
  };

  const handleConfirm = async () => {
    if (confirm?.kind === "delete") await handleDelete(confirm.row);
    else if (confirm?.kind === "privacy-off") {
      setConfirm(null);
      await savePrivacy(false);
    }
  };

  const rows = useMemo(() => {
    if (!cfg) return [];
    const all = data[cfg.key];
    const q = norm(search);
    if (!q) return all;

    return all.filter((x) => {
      const fields = [x[cfg.nameKey]];
      if (cfg.hasDescription) fields.push(x.description);
      if (cfg.hasColor) fields.push(x.color);
      return fields.map(norm).some((v) => v.includes(q));
    });
  }, [cfg, data, search]);

  const isLoading = cfg ? loading[cfg.key] : false;
  const totalCount = cfg ? data[cfg.key].length : 0;

  const navCount = (item) => {
    if (item.key === "privacy") return privacyOn === null ? "…" : privacyOn ? "On" : "Off";
    return data[item.key].length;
  };

  const heroCount = (key) => (loading[key] && data[key].length === 0 ? "…" : data[key].length);

  const handleRefresh = () => {
    if (cfg) fetchList(cfg.key);
  };

  const previewColor = HEX_RE.test(normalizeHex(form.color)) ? normalizeHex(form.color) : "";
  const previewAdjusted = previewColor && accessibleBg(previewColor).toLowerCase() !== previewColor.toLowerCase();

  return (
    <Container fluid className={`min-vh-100 p-3 p-md-4 dcp-page ${isDark ? "dark-theme" : "light-theme"}`}>
      <NavMenu />

      <div className="dcp-hero dcp-fade">
        <div className="dcp-hero-text">
          <span className="dcp-hero-chip"><MdTune /> Workspace settings</span>
          <h1 className="dcp-hero-title">Data Center</h1>
          <p className="dcp-hero-sub">
            Manage the priority, tag and source lists used across the app, and control how AI
            handles your data.
          </p>
        </div>

        <div className="dcp-hero-stats">
          <span className="dcp-hero-stat"><b>{heroCount("priority")}</b> priorities</span>
          <span className="dcp-hero-stat"><b>{heroCount("label")}</b> tags</span>
          <span className="dcp-hero-stat"><b>{heroCount("source")}</b> sources</span>
        </div>
      </div>

      <div className="dcp-layout">
        <nav className="dcp-card dcp-nav dcp-fade" style={{ "--d": "80ms" }} aria-label="Data Center sections">
          <div role="tablist" aria-orientation="vertical" className="dcp-nav-list">
            {NAV.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  type="button"
                  role="tab"
                  id={`dcp-tab-${item.key}`}
                  aria-selected={tab === item.key}
                  aria-controls="dcp-tabpanel"
                  onClick={() => setTab(item.key)}
                  className={`dcp-nav-item ${item.accent} ${tab === item.key ? "active" : ""}`}
                >
                  <span className="dcp-nav-icon"><Icon /></span>
                  <span className="dcp-nav-text">
                    <span className="dcp-nav-label">{item.label}</span>
                  </span>
                  <span className="dcp-nav-count">{navCount(item)}</span>
                </button>
              );
            })}
          </div>
        </nav>

        <div id="dcp-tabpanel" role="tabpanel" aria-labelledby={`dcp-tab-${tab}`} className="dcp-main">
          {tab === "privacy" ? (
            <PrivacyPanel
              enabled={privacyOn}
              loading={loadingPrivacy}
              saving={savingPrivacy}
              error={privacyError}
              onToggle={handleTogglePrivacy}
              onRetry={fetchPrivacy}
            />
          ) : (
            <section className={`dcp-card dcp-panel ${cfg.accent} dcp-fade`} style={{ "--d": "120ms" }}>
              <header className="dcp-panel-head">
                <span className="dcp-panel-icon"><cfg.icon /></span>
                <div className="dcp-panel-titles">
                  <h2 className="dcp-panel-title">{cfg.label} list</h2>
                  <p className="dcp-panel-sub">{cfg.blurb}. Changes apply across the whole app.</p>
                </div>

                <div className="dcp-panel-actions">
                  <Button className="dcp-btn dcp-btn-outline" onClick={handleRefresh} disabled={isLoading}>
                    <IoMdRefresh className="me-2" />
                    Refresh
                  </Button>
                  <Button className="dcp-btn dcp-btn-primary" onClick={openCreate}>
                    <MdAdd className="me-2" />
                    Create {cfg.label}
                  </Button>
                </div>
              </header>

              <div className="dcp-toolbar">
                <div className="dcp-search">
                  <MdSearch className="dcp-search-icon" aria-hidden="true" />
                  <input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={`Search ${cfg.plural}...`}
                    aria-label={`Search ${cfg.plural}`}
                    className="dcp-search-input"
                  />
                  {search && (
                    <button type="button" className="dcp-search-clear" onClick={() => setSearch("")} aria-label="Clear search">
                      <MdClose />
                    </button>
                  )}
                </div>

                <span className="dcp-count">
                  {search ? `${rows.length} of ${totalCount}` : totalCount} {search ? "shown" : "total"}
                </span>
              </div>

              {isLoading && totalCount === 0 ? (
                <div className="dcp-skeletons" aria-busy="true" aria-label={`Loading ${cfg.plural}`}>
                  {[0, 1, 2, 3].map((i) => (
                    <span key={i} className="dcp-skel" />
                  ))}
                </div>
              ) : rows.length === 0 ? (
                <div className="dcp-empty">
                  <span className="dcp-empty-icon"><MdInbox /></span>
                  <div className="dcp-empty-title">
                    {search ? `No ${cfg.plural} match “${search}”` : `No ${cfg.plural} yet`}
                  </div>
                  <div className="dcp-empty-sub">
                    {search ? "Try a different search term." : `Create your first ${cfg.noun} to get started.`}
                  </div>
                  {search ? (
                    <Button className="dcp-btn dcp-btn-outline dcp-btn-sm" onClick={() => setSearch("")}>
                      Clear search
                    </Button>
                  ) : (
                    <Button className="dcp-btn dcp-btn-primary dcp-btn-sm" onClick={openCreate}>
                      <MdAdd className="me-1" /> Create {cfg.label}
                    </Button>
                  )}
                </div>
              ) : (
                <div className={`dcp-table-wrap ${isLoading ? "is-refreshing" : ""}`}>
                  <table className="dcp-table">
                    <thead>
                      <tr>
                        <th className="dcp-col-no">No</th>
                        <th>{cfg.columnLabel}</th>
                        {cfg.hasColor && <th className="dcp-col-color">Color</th>}
                        {cfg.hasDescription && <th>Description</th>}
                        <th className="dcp-col-date">Created</th>
                        <th className="dcp-col-actions">Actions</th>
                      </tr>
                    </thead>

                    <tbody>
                      {rows.map((row, idx) => {
                        const name = row[cfg.nameKey];
                        return (
                          <tr key={cfg.idOf(row) ?? idx}>
                            <td className="dcp-cell-no">{idx + 1}</td>

                            <td>
                              {cfg.hasColor ? (
                                <ColorPill color={row.color}>{name}</ColorPill>
                              ) : (
                                <span className="dcp-name">{name}</span>
                              )}
                            </td>

                            {cfg.hasColor && (
                              <td>
                                {row.color ? (
                                  <span className="dcp-color">
                                    <span className="dcp-color-dot" style={{ background: normalizeHex(row.color) }} />
                                    <code>{normalizeHex(row.color)}</code>
                                  </span>
                                ) : (
                                  "-"
                                )}
                              </td>
                            )}

                            {cfg.hasDescription && (
                              <td className="dcp-cell-desc" title={row.description || undefined}>
                                {row.description || "-"}
                              </td>
                            )}

                            <td className="dcp-cell-date">{formatDDMMYYYY(row.created_at)}</td>

                            <td>
                              <div className="dcp-row-actions">
                                <button
                                  type="button"
                                  className="dcp-icon-btn"
                                  onClick={() => openEdit(row)}
                                  title={`Edit ${name}`}
                                  aria-label={`Edit ${name}`}
                                >
                                  <MdEdit />
                                </button>
                                <button
                                  type="button"
                                  className="dcp-icon-btn danger"
                                  onClick={() => setConfirm({ kind: "delete", row })}
                                  title={`Delete ${name}`}
                                  aria-label={`Delete ${name}`}
                                >
                                  <MdDelete />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </div>
      </div>

      {/* ---------- create / edit ---------- */}
      <Modal
        show={showModal && !!cfg}
        onHide={closeModal}
        centered
        className={isDark ? "dcp-modal-dark" : ""}
      >
        {cfg && (
          <Form onSubmit={handleSave} noValidate>
            <Modal.Header closeButton={!saving} className="dcp-modal-head">
              <Modal.Title className="dcp-modal-title">
                {modalMode === "create" ? "Create" : "Edit"} {cfg.label.toLowerCase()}
              </Modal.Title>
            </Modal.Header>

            <Modal.Body className="dcp-modal-body">
              <Form.Group className="mb-3" controlId="dcp-field-name">
                <Form.Label>{cfg.nameLabel}</Form.Label>
                <Form.Control
                  autoFocus
                  value={form.name}
                  onChange={setField("name")}
                  placeholder={cfg.namePlaceholder}
                  className="dcp-input"
                />
              </Form.Group>

              {cfg.hasColor && (
                <Form.Group className="mb-3" controlId="dcp-field-color">
                  <Form.Label>Color</Form.Label>

                  <div className="dcp-color-row">
                    <Form.Control
                      type="color"
                      value={previewColor.length === 7 ? previewColor : DEFAULT_COLOR}
                      onChange={setField("color")}
                      title="Pick a color"
                      className="dcp-color-picker"
                    />
                    <Form.Control
                      value={form.color}
                      onChange={setField("color")}
                      placeholder={DEFAULT_COLOR}
                      className="dcp-input dcp-hex-input"
                      aria-label="Hex color"
                      spellCheck={false}
                    />
                  </div>

                  <div className="dcp-swatches" role="group" aria-label="Color presets">
                    {COLOR_PRESETS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setField("color")(c)}
                        title={c}
                        aria-label={`Use ${c}`}
                        aria-pressed={previewColor.toLowerCase() === c.toLowerCase()}
                        className={`dc-swatch-btn ${previewColor.toLowerCase() === c.toLowerCase() ? "selected" : ""}`}
                        style={{ background: c }}
                      />
                    ))}
                  </div>

                  <div className="dcp-preview">
                    <span className="dcp-preview-label">Preview</span>
                    <ColorPill color={previewColor || DEFAULT_COLOR}>
                      {form.name.trim() || cfg.label}
                    </ColorPill>
                  </div>
                  {previewAdjusted && (
                    <div className="dcp-hint">
                      Shown slightly adjusted so the text stays easy to read.
                    </div>
                  )}
                </Form.Group>
              )}

              {cfg.hasDescription && (
                <Form.Group controlId="dcp-field-desc">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={form.description}
                    onChange={setField("description")}
                    placeholder="Write description..."
                    className="dcp-input"
                  />
                </Form.Group>
              )}

              {formError && (
                <div className="dcp-inline-error mt-3" role="alert">
                  <MdErrorOutline aria-hidden="true" /> {formError}
                </div>
              )}
            </Modal.Body>

            <Modal.Footer className="dcp-modal-foot">
              <Button type="button" className="dcp-btn dcp-btn-ghost" onClick={closeModal} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" className="dcp-btn dcp-btn-primary" disabled={saving}>
                {saving ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    Saving...
                  </>
                ) : modalMode === "create" ? (
                  `Create ${cfg.label.toLowerCase()}`
                ) : (
                  "Save changes"
                )}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Modal>

      {/* ---------- confirmations ---------- */}
      <ConfirmModal
        isDark={isDark}
        show={confirm?.kind === "delete"}
        title={`Delete ${cfg ? cfg.noun : "item"}?`}
        confirmLabel="Delete"
        busy={confirmBusy}
        onConfirm={handleConfirm}
        onCancel={() => setConfirm(null)}
      >
        {confirm?.kind === "delete" && cfg && (
          <>
            <p className="mb-2">
              <strong>{confirm.row?.[cfg.nameKey]}</strong> will be removed from the list.
            </p>
            <p className="mb-0">This can't be undone.</p>
          </>
        )}
      </ConfirmModal>

      <ConfirmModal
        isDark={isDark}
        show={confirm?.kind === "privacy-off"}
        title="Turn off Sensitive Data Protection?"
        confirmLabel="Turn off"
        busy={confirmBusy}
        onConfirm={handleConfirm}
        onCancel={() => setConfirm(null)}
      >
        AI requests will no longer ask OpenAI not to store them.
      </ConfirmModal>

      {toast && (
        <div className={`dcp-toast ${toast.type}`} role={toast.type === "error" ? "alert" : "status"}>
          {toast.type === "error" ? <MdErrorOutline aria-hidden="true" /> : <MdCheckCircle aria-hidden="true" />}
          <span>{toast.text}</span>
          <button type="button" onClick={() => setToast(null)} aria-label="Dismiss message">
            <MdClose />
          </button>
        </div>
      )}
    </Container>
  );
};

export default DataCenter;
